import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AppProvider } from '@/context/AppContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import { Analytics } from '@/components/Analytics';
import { MetaPixelTracker } from '@/components/MetaPixelTracker';
import LandingPage from '@/pages/LandingPage';
import GigCatalog from '@/pages/GigCatalog';
import GigDetail from '@/pages/GigDetail';
import ClientDashboard from '@/pages/ClientDashboard';
import PaymentSuccess from '@/pages/PaymentSuccess';
import AdminDashboard from '@/pages/AdminDashboard';
import AuthPage from '@/pages/AuthPage';
import BlogCatalog from '@/pages/BlogCatalog';
import BlogDetail from '@/pages/BlogDetail';
import OfferCheckout from '@/pages/OfferCheckout';
import LandingPageRoute from '@/pages/LandingPageRoute';
import InvoicePage from '@/pages/InvoicePage';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/* ───────────────────────────────────────────────────────────────────────────
   ScrollManager — fixes "lands mid-page" navigation.
   • New route (PUSH/REPLACE) without hash  → scroll to top.
   • Hash navigation                        → scroll to section (sticky-header
     offset handled via CSS scroll-margin-top on section[id]).
   • Browser Back/Forward (POP)             → leave scroll to the browser's
     native restoration (do not force).
   Depends only on pathname + hash so dashboard tabs/modals/query-param changes
   (which keep the same pathname) never reset the scroll position.
   ─────────────────────────────────────────────────────────────────────────── */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'

  useEffect(() => {
    if (hash) {
      // Always honour an explicit hash target (including direct URL loads /
      // back-forward), since the browser's native jump can race with React render.
      const id = decodeURIComponent(hash.slice(1));
      const jump = () => {
        const el = id && document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };
      if (!jump()) {
        const t = setTimeout(jump, 160);
        return () => clearTimeout(t);
      }
      return;
    }

    if (navType === 'POP') return; // back/forward without a hash: preserve restoration

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash, navType]);

  return null;
}

/* Layout wrapper: hides Navbar/Footer on admin routes */
function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isLandingFunnel = location.pathname === '/landing-page';
  const hideChrome = isAdmin || isLandingFunnel;

  return (
    <div className={hideChrome ? '' : 'min-h-screen bg-background text-foreground'}>
      <ScrollManager />
      {!hideChrome && <Navbar />}
      <Analytics />
      <MetaPixelTracker />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/services" element={<GigCatalog />} />
        <Route path="/services/:id" element={<GigDetail />} />
        <Route path="/dashboard" element={<ClientDashboard />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/blog" element={<BlogCatalog />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/checkout/:offerId" element={<OfferCheckout />} />
        <Route path="/invoice/:invoiceId" element={<InvoicePage />} />
        <Route path="/landing-page" element={<LandingPageRoute />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
      </Routes>
      {!hideChrome && <Footer />}
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AppProvider>
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </AppProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
